import React from "react";
import _ from "lodash";

import {
  AnalyticsCard,
  AnalyticsTable,
  Cell,
  ColumnDef,
  LeadsAreaChart,
  TABLE_MIN_HEIGHT,
  TableFooter,
  PageAnalyticsChart,
  VisitorProfileCell,
} from "../../common";
import {
  useLandingPagesQuery,
  useRowTrendQuery,
  useSessionsQuery,
  useSessionsTimeSeriesQuery,
} from "src/query/experiences/leadsAndReports/reports";
import { useTableState } from "../../hooks/useTableState";
import { useStablePagination } from "../../hooks/useStablePagination";
import { formatDurationWithLabel } from "../../utils/format";
import { isInteractive } from "../../constants";
import { ReportRequest } from "src/types/experiences/leadsAndReports";
import styles from "../../common/common.module.scss";

interface SessionAnalyticsProps {
  storyboardId: string;
  storyboardType?: string;
}

const COLUMNS: ColumnDef[] = [
  { key: "visitor", header: "Visitor Details\n& Date" },
  { key: "source", header: "Source, Medium\n& Page landed on" },
  { key: "geo", header: "Geography\n& Device" },
  { key: "time", header: "Time &\nCompletion %", align: "right" },
  { key: "page", header: "Page Analytics", align: "right-fill" },
];

const renderSource = (data: any) => {
  if (!data.campaign) return "-";
  const source = data.campaign.source || "";
  const medium = data.campaign.medium || "";
  return [source, medium].filter(Boolean).join(", ") || "-";
};

const renderLocation = (data: any) => {
  const city = _.get(data, "country.city");
  if (!city) return "UNKNOWN";
  return `${city}, ${_.get(data, "country.region_name")}, ${_.get(data, "country.country_code")}`;
};

/** Per-row container: fires the trend query for each session row. */
const SessionRow: React.FC<{
  row: any;
  variant: "interactive" | "landing";
  request: ReportRequest;
  cacheKey: string;
  typeReady: boolean;
  pages: any[];
}> = ({ row, variant, request, cacheKey, typeReady, pages }) => {
  const { data: trend } = useRowTrendQuery(
    variant,
    "sessions",
    row.visitorSession ?? null,
    { ...request, visitorSession: row.visitorSession },
    cacheKey,
    Boolean(row.visitorSession) && typeReady,
    pages,
  );

  return (
    <>
      <VisitorProfileCell data={row} type="sessions" />
      <Cell headline={renderSource(row)} description={row.url} />
      <Cell headline={renderLocation(row)} description={`${row.browser} on ${row.device}`} />
      <Cell
        headline={formatDurationWithLabel(row.timeSpent).value}
        description={`${row.completion}%`}
      />
      <div>
        {trend && trend.length > 0 ? (
          <PageAnalyticsChart data={trend} valueKey="views" />
        ) : (
          <span>-</span>
        )}
      </div>
    </>
  );
};

export const SessionAnalytics: React.FC<SessionAnalyticsProps> = ({ storyboardId, storyboardType }) => {
  const { request, key, page, setPage, chart, setChart, enabled } = useTableState(storyboardId);
  const typeReady = storyboardType !== undefined;
  const variant = isInteractive(storyboardType) ? "interactive" : "landing";

  const { data, isLoading } = useSessionsQuery(request, key, page, enabled);
  const { data: timeSeries, isLoading: isChartLoading } = useSessionsTimeSeriesQuery(
    request,
    key,
    enabled,
  );
  const { data: pages = [] } = useLandingPagesQuery(storyboardId, variant === "landing");
  const rows = data?.records ?? [];
  const stablePagination = useStablePagination(data);
  const paginated = (stablePagination?.totalPages ?? 1) > 1;
  const views = Array.isArray(timeSeries) ? timeSeries : [];

  const renderChart = () => {
    switch (chart) {
      case "graph_view":
        return (
          <div className={styles.chartFull}>
            <LeadsAreaChart
              data={views}
              height={380}
              loading={isChartLoading}
              legend={{ key: "Date", value: "Sessions" }}
            />
          </div>
        );
      case "table_view":
        return (
          <AnalyticsTable
            columns={COLUMNS}
            rows={rows}
            loading={isLoading}
            contentMinHeight={paginated ? TABLE_MIN_HEIGHT.full : undefined}
            gridTemplate="20fr 35fr 13fr 10fr 22fr"
            renderRow={(row: any) => (
              <SessionRow row={row} variant={variant} request={request} cacheKey={key} typeReady={typeReady} pages={pages} />
            )}
          />
        );
      default:
        return null;
    }
  };

  return (
    <AnalyticsCard
      title="Session Analytics"
      count={data?.totalRecordsCount ?? rows.length}
      currentChart={chart}
      disableView={["pie_chart_view"]}
      onChartChange={setChart}
      minHeight={TABLE_MIN_HEIGHT.full}
      footer={
        <TableFooter
          currentChart={chart}
          handlePageChange={setPage}
          pagination={data}
          paginationName="Sessions"
        />
      }
    >
      {renderChart()}
    </AnalyticsCard>
  );
};
